import { type ReactNode } from 'react';
import { renderOverflowTooltipLabel } from './components';
import { type Maybe } from './maybe';

type PlaceholderTemplateProps = {
    placeholder?: string;
};

type OverflowItemRenderer<T, TArgs extends unknown[] = []> = (
    item: T,
    ...args: TArgs
) => ReactNode;
type OverflowValueRenderer<T, TArgs extends unknown[] = []> = (
    item: T,
    templateProps: PlaceholderTemplateProps,
    ...args: TArgs
) => ReactNode;

type OverflowItemTemplate<T, TArgs extends unknown[] = []> = Maybe<
    ReactNode | OverflowItemRenderer<T, TArgs>
>;
type OverflowValueTemplate<T, TArgs extends unknown[] = []> = Maybe<
    ReactNode | OverflowValueRenderer<T, TArgs>
>;

export function createOverflowItemTemplate<T, TArgs extends unknown[] = []>({
    enabled,
    getLabel,
    className,
    fallbackTemplate,
}: {
    enabled: boolean;
    getLabel: (item: T) => Maybe<string>;
    className: string;
    fallbackTemplate?: OverflowItemTemplate<T, TArgs>;
}): OverflowItemTemplate<T, TArgs> {
    if (!enabled) {
        return fallbackTemplate;
    }

    return (item: T, ...args: TArgs): ReactNode => {
        void args;

        return renderOverflowTooltipLabel(getLabel(item), className);
    };
}

export function createOverflowValueTemplate<T, TArgs extends unknown[] = []>({
    enabled,
    getLabel,
    className,
    fallbackTemplate,
    renderEmpty,
}: {
    enabled: boolean;
    getLabel: (item: T) => Maybe<string>;
    className: string;
    fallbackTemplate?: OverflowValueTemplate<T, TArgs>;
    renderEmpty?: (templateProps: PlaceholderTemplateProps) => ReactNode;
}): OverflowValueTemplate<T, TArgs> {
    if (!enabled) {
        return fallbackTemplate;
    }

    return (
        item: T,
        templateProps: PlaceholderTemplateProps,
        ...args: TArgs
    ): ReactNode => {
        void args;

        if (item === null || typeof item === 'undefined') {
            return renderEmpty ? renderEmpty(templateProps) : null;
        }

        return renderOverflowTooltipLabel(getLabel(item), className);
    };
}
